import React, { useEffect, useRef, useState } from 'react';
import { App, Button, Form, Modal, Select, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import SupplierForm from '../../../components/suppliers/SupplierForm';
import { applySupplierSaveError } from '../../../components/suppliers/supplierFormErrors';
import type { SupplierFormValues, SupplierSubmitValues } from '../../suppliers/types';
import { createSupplier } from '../../reference-data/api/referenceDataApi';
import type { SupplierOrderDto } from '../../../dto/supplierOrder';
import type { SupplierOptionDto } from '../../../dto/reference';
import { getSupplierOrderDisplayNumber, updateSupplierOrder } from '../api/supplierOrdersApi';
import {
  buildChooseSupplierUpdateData,
  resolveSupplierDefaultCurrency,
  shouldApplySupplierCurrency,
} from '../supplierOrderCurrency';
import { ORDER_CURRENCIES } from '../currencies';
import { getSupplierOrderErrorMessage, isBlockingSupplierOrderError } from '../supplierOrderErrorMessages';
import { useFeedback } from '../../../utils/feedback';

const { Paragraph } = Typography;

interface SupplierOrderChooseSupplierModalProps {
  open: boolean;
  supplierOrder: SupplierOrderDto;
  suppliers: SupplierOptionDto[];
  /** Purchase-price editors also set the ZD currency (supplier default). */
  canEditPurchasePrices: boolean;
  onClose: () => void;
  onSaved: () => Promise<unknown> | void;
  /**
   * A ZD that is not saved yet (`/app/supplier-orders/new`): the choice is the
   * first save and creates the ZD (karta ZD według makiety). Returns true when
   * it was saved. Without it the choice is a PUT of the existing ZD.
   */
  onSubmitChoice?: (supplierId: string, currency: string | null) => Promise<boolean>;
  /**
   * „Dodaj dostawcę” (kept from the former ZD wizard): a supplier created here
   * is chosen at once; the page adds it to its own supplier list.
   */
  onSupplierCreated?: (supplier: SupplierOptionDto) => void;
}

/**
 * „Wybierz dostawcę” of a confirmation draft ZD (usability round 2026-10-05):
 * the choice opens right here, over the card, instead of an inline editor in a
 * collapsed section at the bottom of the page. The ZD currency follows the
 * supplier's default currency until the operator picks another one
 * (modules/orders/supplierOrderCurrency.ts).
 */
const SupplierOrderChooseSupplierModal: React.FC<SupplierOrderChooseSupplierModalProps> = ({
  open, supplierOrder, suppliers: supplierOptions, canEditPurchasePrices, onClose, onSaved, onSubmitChoice, onSupplierCreated,
}) => {
  const { t } = useTranslation(['orders', 'common', 'suppliers']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const [form] = Form.useForm<{ supplierId?: string; currency?: string }>();
  const [saving, setSaving] = useState(false);
  const [supplierForm] = Form.useForm<SupplierFormValues>();
  const [addOpen, setAddOpen] = useState(false);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [createdSuppliers, setCreatedSuppliers] = useState<SupplierOptionDto[]>([]);
  const suppliers = [
    ...supplierOptions,
    ...createdSuppliers.filter((created) => !supplierOptions.some((supplier) => supplier.id === created.id)),
  ];
  const currencyTouched = useRef(false);
  const supplierId = Form.useWatch('supplierId', form);
  const selectedSupplier = suppliers.find((supplier) => supplier.id === supplierId) || null;
  const supplierCurrency = resolveSupplierDefaultCurrency(selectedSupplier);
  const orderNumber = getSupplierOrderDisplayNumber(supplierOrder) || t('orders:supplierOrders.card.newNumber');

  // The form mounts with the window (destroyOnHidden), so the current values
  // are its initialValues: a setFieldsValue in an effect ran before the form
  // existed when the window was opened later (e.g. „Wybierz dostawcę” on the
  // stage panel) and left the currency empty.
  const initialValues = {
    supplierId: supplierOrder.supplierId || undefined,
    currency: supplierOrder.currency || 'PLN',
  };
  useEffect(() => {
    if (open) currencyTouched.current = false;
  }, [open]);

  const handleSupplierChange = (nextSupplierId: string) => {
    const nextSupplier = suppliers.find((supplier) => supplier.id === nextSupplierId);
    const nextCurrency = resolveSupplierDefaultCurrency(nextSupplier);
    if (shouldApplySupplierCurrency({
      canEditPurchasePrices,
      currencyTouched: currencyTouched.current,
      supplierCurrency: nextCurrency,
      currentCurrency: form.getFieldValue('currency'),
    })) {
      form.setFieldValue('currency', nextCurrency);
    }
  };

  const createAndChooseSupplier = async (values: SupplierSubmitValues) => {
    setAddSubmitting(true);
    try {
      const created = await createSupplier(values as unknown as Record<string, unknown>);
      feedback.success(t('suppliers:messages.createSuccess', { name: created.name }));
      setCreatedSuppliers((current) => [...current, created]);
      onSupplierCreated?.(created);
      setAddOpen(false);
      form.setFieldValue('supplierId', created.id);
      const nextCurrency = resolveSupplierDefaultCurrency(created);
      if (shouldApplySupplierCurrency({
        canEditPurchasePrices,
        currencyTouched: currencyTouched.current,
        supplierCurrency: nextCurrency,
        currentCurrency: form.getFieldValue('currency'),
      })) {
        form.setFieldValue('currency', nextCurrency);
      }
    } catch (error) {
      applySupplierSaveError(error, supplierForm, t, feedback, t('suppliers:messages.saveFailed', { name: values.name }));
    } finally {
      setAddSubmitting(false);
    }
  };

  const save = async () => {
    let values: { supplierId?: string; currency?: string };
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    if (!values.supplierId) return;
    setSaving(true);
    if (onSubmitChoice) {
      try {
        // The new-ZD flow owns its own success/error feedback and navigation.
        if (await onSubmitChoice(values.supplierId, canEditPurchasePrices ? values.currency || null : null)) onClose();
      } finally {
        setSaving(false);
      }
      return;
    }
    try {
      const saved = await updateSupplierOrder(
        supplierOrder.id,
        buildChooseSupplierUpdateData(supplierOrder, values.supplierId, canEditPurchasePrices ? values.currency : null),
        { modal, t },
      );
      if (!saved) return;
      feedback.success(t('orders:supplierOrders.drafts.chooseSupplierSuccess', {
        supplier: suppliers.find((supplier) => supplier.id === values.supplierId)?.name || '',
        number: orderNumber,
      }));
      onClose();
      await onSaved();
    } catch (error) {
      feedback.error(getSupplierOrderErrorMessage(error, t, 'orders:supplierOrders.detail.updateError', {
        orderNumber,
      }), { sticky: isBlockingSupplierOrderError(error) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={t('orders:supplierOrders.drafts.chooseSupplierTitle', { number: orderNumber })}
      okText={t('orders:supplierOrders.drafts.chooseSupplierSave')}
      cancelText={t('common:cancel')}
      okButtonProps={{ loading: saving, 'data-testid': 'supplier-order-choose-supplier-save' } as React.ComponentProps<typeof Modal>['okButtonProps']}
      onOk={() => { void save(); }}
      onCancel={() => { if (!saving) onClose(); }}
      destroyOnHidden
    >
      <div data-testid="supplier-order-choose-supplier-modal">
      <Paragraph type="secondary">
        {t(onSubmitChoice ? 'orders:supplierOrders.card.chooseSupplierSavesDraft' : 'orders:supplierOrders.drafts.chooseSupplierIntro')}
      </Paragraph>
      <Form form={form} layout="vertical" preserve={false} initialValues={initialValues}>
        <Form.Item
          name="supplierId"
          label={t('orders:supplierOrders.form.supplier')}
          rules={[{ required: true, message: t('orders:supplierOrders.drafts.errors.supplierRequired') }]}
          extra={(
            <Button
              type="link"
              size="small"
              icon={<PlusOutlined />}
              style={{ paddingInline: 0 }}
              onClick={() => { supplierForm.resetFields(); setAddOpen(true); }}
              data-testid="supplier-order-add-supplier"
            >
              {t('orders:supplierOrders.form.addSupplier')}
            </Button>
          )}
        >
          <Select
            autoFocus
            showSearch
            defaultOpen={!supplierOrder.supplierId}
            optionFilterProp="searchText"
            placeholder={t('orders:supplierOrders.drafts.chooseSupplierPlaceholder')}
            options={suppliers.map((supplier) => ({ value: supplier.id, label: supplier.name, searchText: supplier.searchText }))}
            onChange={handleSupplierChange}
            data-testid="supplier-order-choose-supplier-select"
          />
        </Form.Item>
        {canEditPurchasePrices ? (
          <Form.Item
            name="currency"
            label={t('orders:supplierOrders.drafts.chooseSupplierCurrency')}
            extra={selectedSupplier && supplierCurrency
              ? t('orders:supplierOrders.drafts.chooseSupplierCurrencyHint', {
                supplier: selectedSupplier.name,
                currency: supplierCurrency,
              })
              : t('orders:supplierOrders.drafts.chooseSupplierCurrencyHintNoSupplier')}
            rules={[{ required: true }]}
          >
            <Select
              style={{ maxWidth: 160 }}
              options={ORDER_CURRENCIES.map((currency) => ({ value: currency, label: currency }))}
              onChange={() => { currencyTouched.current = true; }}
              data-testid="supplier-order-choose-supplier-currency"
            />
          </Form.Item>
        ) : null}
      </Form>
      </div>
      <Modal
        title={t('orders:supplierOrders.form.addSupplier')}
        open={addOpen}
        onCancel={() => { if (!addSubmitting) setAddOpen(false); }}
        maskClosable={false}
        footer={null}
        width={600}
        destroyOnHidden
      >
        <SupplierForm
          form={supplierForm}
          editingSupplier={null}
          submitting={addSubmitting}
          submitLabel={t('orders:supplierOrders.form.addSupplierSubmit')}
          onSubmit={createAndChooseSupplier}
          open={addOpen}
          onCancel={() => setAddOpen(false)}
        />
      </Modal>
    </Modal>
  );
};

export default SupplierOrderChooseSupplierModal;
